/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import useFacility from '../../hooks/useFacility';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Collapse,
  CircularProgress,
  Alert,
  Tooltip,
  Tabs,
  Tab,
  TablePagination
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  RestartAlt as RestartAltIcon
} from '@mui/icons-material';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Pending', label: 'Pending Review' },
  { value: 'Rejected', label: 'Rejected' }
];

const STATUS_COLORS = {
  'In Progress': 'primary',
  'Requires Extension': 'warning',
  'Completed': 'success',
  'Approved': 'info',
  'Rejected': 'error'
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const StatusChip = ({ status }) => (
  <Chip label={status} size="small" color={STATUS_COLORS[status] || 'default'} variant="outlined" />
);

const KpiCard = ({ label, value, hint, bgcolor = '#f5f5f5', color }) => (
  <Paper sx={{ p: 2, minWidth: 150, flex: '1 1 150px', bgcolor }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h5" sx={{ fontWeight: 600, color }}>{value ?? '—'}</Typography>
    {hint && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{hint}</Typography>}
  </Paper>
);

const headerRowSx = { backgroundColor: '#f5f5f5', '& .MuiTableCell-root': { fontWeight: 600 } };

// ── CSV helpers ──

const csvCell = (value) => {
  const s = value == null ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (filename, header, rows) => {
  const content = [header, ...rows].map(r => r.map(csvCell).join(',')).join('\n');
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ── Expandable rows ──

const PiRow = ({ row }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={open ? 'Hide projects' : 'Show projects'} onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{row.pi_name || '—'}</TableCell>
        <TableCell>{row.pi_email || '—'}</TableCell>
        <TableCell>{row.project_count}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {Object.entries(row.status_counts).map(([status, count]) => (
              <Chip key={status} size="small" variant="outlined" color={STATUS_COLORS[status] || 'default'} label={`${status}: ${count}`} />
            ))}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Proposal ID</TableCell>
                    <TableCell>Project Title</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.projects.map(p => (
                    <TableRow key={p._id}>
                      <TableCell>{p.project_id}</TableCell>
                      <TableCell>{p.project_title}</TableCell>
                      <TableCell><StatusChip status={p.project_status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const SpeciesRow = ({ row }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={open ? 'Hide projects' : 'Show projects'} onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          {row.no_species ? <i>{row.name}</i> : row.name}
          {row.no_species && (
            <Tooltip title="These projects' applications don't name any species.">
              <Chip label="No species on application" size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
            </Tooltip>
          )}
          {!row.catalogued && !row.no_species && (
            <Tooltip title="Entered by the applicant as 'species not listed'; not in the species catalog.">
              <Chip label="Not in catalog" size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
            </Tooltip>
          )}
        </TableCell>
        <TableCell><i>{row.scientific_name || '—'}</i></TableCell>
        <TableCell>{row.project_count}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={4} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Proposal ID</TableCell>
                    <TableCell>Project Title</TableCell>
                    <TableCell>PI</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.projects.map(p => (
                    <TableRow key={p._id}>
                      <TableCell>{p.project_id}</TableCell>
                      <TableCell>{p.project_title}</TableCell>
                      <TableCell>{p.pi_name || p.pi_email || '—'}</TableCell>
                      <TableCell><StatusChip status={p.project_status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ── Main component ──

const TABS = [
  { key: 'projects', label: 'Projects & PIs' },
  { key: 'by-pi', label: 'Projects per PI' },
  { key: 'by-species', label: 'Projects per Species' },
  { key: 'requests', label: 'Requests' },
  { key: 'users', label: 'New Users' }
];

const ProjectsReport = () => {
  const { facilityPreference, loading: prefLoading } = useFacility();

  const [report, setReport] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedFacility, setSelectedFacility] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('all');
  const [type, setType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [activeTab, setActiveTab] = useState('projects');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    if (!prefLoading && facilityPreference) setSelectedFacility(facilityPreference);
  }, [facilityPreference, prefLoading]);

  useEffect(() => {
    axios.get('/api/facilities')
      .then(res => setFacilities(Array.isArray(res.data) ? res.data : []))
      .catch(err => console.error('Failed to fetch facilities:', err));
  }, []);

  const fetchReport = useCallback(async () => {
    if (prefLoading) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (selectedFacility !== 'all') params.append('facility', selectedFacility);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (status !== 'all') params.append('status', status);
      params.append('type', type);
      const res = await axios.get(`/api/admin/reports/projects?${params.toString()}`);
      setReport(res.data);
    } catch (err) {
      console.error('Error fetching projects report:', err);
      setError('Failed to load the projects report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [prefLoading, selectedFacility, startDate, endDate, status, type]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    setPage(0);
  }, [activeTab, searchQuery, report]);

  const q = searchQuery.trim().toLowerCase();
  const matches = (...values) => !q || values.some(v => v && String(v).toLowerCase().includes(q));

  const rows = useMemo(() => {
    if (!report) return [];
    switch (activeTab) {
      case 'projects':
        return report.projects.filter(p => matches(
          p.project_id, p.project_title, p.facility_name, p.pi_name, p.pi_email, p.project_status,
          ...p.species.map(s => s.name), ...p.species.map(s => s.scientific_name)
        ));
      case 'by-pi':
        return report.by_pi.filter(r => matches(r.pi_name, r.pi_email, ...r.projects.map(p => p.project_title)));
      case 'by-species':
        return report.by_species.filter(r => matches(r.name, r.scientific_name));
      case 'requests':
        return report.requests_by_type.filter(r => matches(r.label));
      case 'users':
        return report.new_users.filter(u => matches(u.name, u.email, u.facility_name));
      default:
        return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, activeTab, q]);

  const pagedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const periodLabel = startDate || endDate
    ? `${startDate ? formatDate(`${startDate}T00:00:00`) : 'Start'} – ${endDate ? formatDate(`${endDate}T00:00:00`) : 'Today'}`
    : 'All time';

  const handleExport = () => {
    const stamp = new Date().toISOString().split('T')[0];
    switch (activeTab) {
      case 'projects':
        downloadCsv(`projects-report-${stamp}.csv`,
          ['Proposal ID', 'Project Title', 'Facility', 'PI Name', 'PI Email', 'Species', 'Start Date', 'End Date', 'Status'],
          rows.map(p => [p.project_id, p.project_title, p.facility_name, p.pi_name, p.pi_email,
            p.species.map(s => s.name).join('; '), formatDate(p.project_start_date), formatDate(p.project_end_date), p.project_status]));
        break;
      case 'by-pi':
        downloadCsv(`projects-per-pi-${stamp}.csv`,
          ['PI Name', 'PI Email', 'Proposal ID', 'Project Title', 'Status'],
          rows.flatMap(r => r.projects.map(p => [r.pi_name, r.pi_email, p.project_id, p.project_title, p.project_status])));
        break;
      case 'by-species':
        downloadCsv(`projects-per-species-${stamp}.csv`,
          ['Species', 'Scientific Name', 'In Catalog', 'Proposal ID', 'Project Title', 'PI', 'Status'],
          rows.flatMap(r => r.projects.map(p => [r.name, r.scientific_name, r.no_species ? '' : (r.catalogued ? 'Yes' : 'No'),
            p.project_id, p.project_title, p.pi_name || p.pi_email, p.project_status])));
        break;
      case 'requests':
        downloadCsv(`requests-report-${stamp}.csv`,
          ['Request Type', 'Total', 'New', 'Pending', 'Completed', 'Rejected'],
          rows.map(r => [r.label, r.total, r.New, r.Pending, r.Completed, r.Rejected]));
        break;
      case 'users':
        downloadCsv(`new-users-${stamp}.csv`,
          ['Name', 'Email', 'Facility', 'Status', 'Registered'],
          rows.map(u => [u.name, u.email, u.facility_name, u.status, formatDate(u.registered_at)]));
        break;
      default:
    }
  };

  const summary = report?.summary;

  const renderTable = () => {
    if (rows.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          No results for the selected filters.
        </Box>
      );
    }

    switch (activeTab) {
      case 'projects':
        return (
          <Table size="small">
            <TableHead>
              <TableRow sx={headerRowSx}>
                <TableCell>Proposal ID</TableCell>
                <TableCell>Project Title</TableCell>
                <TableCell>Facility</TableCell>
                <TableCell>PI</TableCell>
                <TableCell>Species</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map(p => (
                <TableRow key={p._id} hover>
                  <TableCell>{p.project_id}</TableCell>
                  <TableCell>{p.project_title}</TableCell>
                  <TableCell>{p.facility_name || '—'}</TableCell>
                  <TableCell>
                    <div>{p.pi_name || '—'}</div>
                    {p.pi_email && <Typography variant="caption" color="text.secondary">{p.pi_email}</Typography>}
                  </TableCell>
                  <TableCell>{p.species.length ? p.species.map(s => s.name).join(', ') : '—'}</TableCell>
                  <TableCell>{formatDate(p.project_start_date)}</TableCell>
                  <TableCell>{formatDate(p.project_end_date)}</TableCell>
                  <TableCell><StatusChip status={p.project_status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );
      case 'by-pi':
        return (
          <Table size="small">
            <TableHead>
              <TableRow sx={headerRowSx}>
                <TableCell padding="checkbox" />
                <TableCell>PI Name</TableCell>
                <TableCell>PI Email</TableCell>
                <TableCell>Projects</TableCell>
                <TableCell>By Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map(r => <PiRow key={r.key} row={r} />)}
            </TableBody>
          </Table>
        );
      case 'by-species':
        return (
          <Table size="small">
            <TableHead>
              <TableRow sx={headerRowSx}>
                <TableCell padding="checkbox" />
                <TableCell>Species</TableCell>
                <TableCell>Scientific Name</TableCell>
                <TableCell>Projects</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map(r => <SpeciesRow key={r.key} row={r} />)}
            </TableBody>
          </Table>
        );
      case 'requests':
        return (
          <Table size="small">
            <TableHead>
              <TableRow sx={headerRowSx}>
                <TableCell>Request Type</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>New</TableCell>
                <TableCell>Pending</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>Rejected</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map(r => (
                <TableRow key={r.key} hover>
                  <TableCell>{r.label}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{r.total}</TableCell>
                  <TableCell>{r.New || 0}</TableCell>
                  <TableCell>{r.Pending || 0}</TableCell>
                  <TableCell>{r.Completed || 0}</TableCell>
                  <TableCell>{r.Rejected || 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );
      case 'users':
        return (
          <Table size="small">
            <TableHead>
              <TableRow sx={headerRowSx}>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Facility</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Registered</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map(u => (
                <TableRow key={u._id} hover>
                  <TableCell>{u.name || '—'}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.facility_name || '—'}</TableCell>
                  <TableCell>{u.status}</TableCell>
                  <TableCell>{formatDate(u.registered_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );
      default:
        return null;
    }
  };

  const tabCount = (key) => {
    if (!report) return '';
    switch (key) {
      case 'projects': return report.projects.length;
      case 'by-pi': return report.by_pi.length;
      case 'by-species': return summary.species_studied;
      case 'requests': return summary.requests.total;
      case 'users': return report.new_users.length;
      default: return '';
    }
  };

  const tabHelp = {
    'projects': 'Every project in the period that matches the status filter, with its PI and the species it studies.',
    'by-pi': 'Projects grouped by Principal Investigator. Expand a row to see that PI\'s projects.',
    'by-species': 'Projects grouped by the species they study (e.g. corn, soybean). A project studying several species appears under each; projects whose application names no species are grouped under "No species listed". Expand a row to see its projects.',
    'requests': 'Requests submitted during the period, by type and current status. Not affected by the status or application type filters.',
    'users': 'User accounts registered during the period. Not affected by the facility, status or application type filters.'
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 600 }}>
        Projects Report
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {periodLabel}. A project counts toward the period when its start-to-end span overlaps it
        (projects with no start date use their submission date; no end date means ongoing).
      </Typography>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Facility</InputLabel>
          <Select value={selectedFacility} label="Facility" onChange={(e) => setSelectedFacility(e.target.value)}>
            <MenuItem value="all">All Facilities</MenuItem>
            {facilities.map(f => (
              <MenuItem key={f._id} value={f._id}>{f.facility_name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="From"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        {(startDate || endDate) && (
          <Tooltip title="Clear date filter">
            <IconButton onClick={() => { setStartDate(''); setEndDate(''); }} size="small">
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Status</InputLabel>
          <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel>Application Type</InputLabel>
          <Select value={type} label="Application Type" onChange={(e) => setType(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="project">Projects</MenuItem>
            <MenuItem value="resource_only">Resource Only</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ flex: 1 }} />

        <Tooltip title="Refresh data">
          <span>
            <IconButton onClick={fetchReport} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {!report && loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
      )}

      {report && (
        <>
          {/* KPI cards */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, opacity: loading ? 0.6 : 1 }}>
            <KpiCard label="Total Projects" value={summary.total_projects} bgcolor="#e3f2fd"
              hint={status === 'all' ? 'All statuses' : STATUS_OPTIONS.find(o => o.value === status)?.label} />
            <KpiCard label="PIs" value={summary.pis} />
            <KpiCard label="Species Studied" value={summary.species_studied} bgcolor="#f1f8e9"
              hint={summary.projects_without_species ? `${summary.projects_without_species} project(s) with no species listed` : null} />
            <KpiCard label="Requests" value={summary.requests.total} bgcolor="#fff3e0"
              hint={`${summary.requests.Pending || 0} pending · ${summary.requests.Approved || 0} approved`} />
            <KpiCard label="New Users" value={summary.new_users} bgcolor="#fce4ec" hint="Registered in period" />
          </Box>

          {/* Tabs */}
          <Paper>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              {TABS.map(t => <Tab key={t.key} value={t.key} label={`${t.label} (${tabCount(t.key)})`} />)}
            </Tabs>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', p: 2 }}>
              <TextField
                placeholder={activeTab === 'by-species' ? 'Search species, e.g. corn or soybean...' : 'Search...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                sx={{ width: 320 }}
                InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} /> }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 240 }}>
                {tabHelp[activeTab]}
              </Typography>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExport} disabled={rows.length === 0}>
                Export CSV
              </Button>
            </Box>

            <TableContainer sx={{ opacity: loading ? 0.6 : 1 }}>
              {renderTable()}
            </TableContainer>

            {rows.length > 0 && (
              <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ProjectsReport;
